import { useMemo, useState, type ReactNode } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart3, CheckCircle2, Clock, Users } from 'lucide-react';
import { Bar, BarChart, Cell, Pie, PieChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useLocalization } from '@/hooks/useLocalization';
import { OWNED_TYPES, useTeamDirectory, type OwnedType, type TeamPerson } from '@/hooks/useTeamDirectory';

const TYPE_LABEL: Record<OwnedType, string> = {
  deliverable: 'Deliverables', work_package: 'Work packages', milestone: 'Milestones', risk: 'Risks', issue: 'Issues', dependency: 'Dependencies',
};
const PIE_COLORS = ['hsl(var(--primary))', '#7c5cbf', '#5b3fc4', '#8b6fd6', '#a48be0', '#c4b5ea'];
const tooltipStyle = { backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' };

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
/** Load is relative to the busiest person, because registers hold item counts, not hours. */
const loadPct = (p: TeamPerson, busiest: number) => (busiest > 0 ? Math.round((p.open / busiest) * 100) : 0);
const loadColor = (pct: number) => (pct >= 90 ? 'text-rose-500' : pct >= 70 ? 'text-amber-500' : 'text-emerald-500');
const shortName = (name: string) => { const w = name.split(/\s+/); return w.length > 1 ? `${w[0]} ${w[w.length - 1][0]}.` : name; };

function Stat({ icon: Icon, tone, label, value }: { icon: typeof Users; tone: string; label: string; value: string | number }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`rounded-lg p-2 ${tone}`}><Icon className="h-5 w-5" aria-hidden="true" /></div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold text-foreground">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MemberCard({ person, busiest, onOpen }: { person: TeamPerson; busiest: number; onOpen: () => void }) {
  const { t, tx } = useLocalization();
  const pct = loadPct(person, busiest);
  return (
    <button type="button" onClick={onOpen} className="rounded-lg border bg-card p-5 text-start transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={person.name}>
      <div className="flex items-start gap-4">
        <Avatar className="h-14 w-14 border-2 border-border"><AvatarFallback className="bg-primary/10 font-semibold text-primary">{initials(person.name)}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-foreground">{tx(person.name)}</h3>
          <p className="truncate text-sm text-muted-foreground">{tx(person.primaryRole)}</p>
          {person.portfolio && <Badge variant="outline" className="mt-2 max-w-full truncate text-xs">{tx(person.portfolio)}</Badge>}
        </div>
      </div>
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('Workload')}</span>
          <span className={`font-medium ${loadColor(pct)}`}>{person.open} {t('open')}</span>
        </div>
        <Progress value={pct} className="h-2" />
        <div className="flex items-center justify-between border-t border-border pt-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-emerald-500" aria-hidden="true" />{person.done}</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-amber-500" aria-hidden="true" />{person.open}</span>
          <span className="flex items-center gap-1"><Users className="h-4 w-4 text-primary" aria-hidden="true" />{person.projects.length} {t('projects')}</span>
        </div>
      </div>
    </button>
  );
}

function MemberDetail({ person }: { person: TeamPerson }) {
  const { t, tx } = useLocalization();
  const byPortfolio = useMemo(() => {
    const map = new Map<string, TeamPerson['projects']>();
    for (const p of person.projects) {
      const k = p.portfolio ?? t('No portfolio');
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(p);
    }
    return [...map];
  }, [person, t]);
  return (
    <div className="space-y-6">
      <section aria-label={t('What they own')}>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('What they own')}</h3>
        <div className="space-y-2">
          {OWNED_TYPES.filter((k) => person.owned[k].open + person.owned[k].done > 0).map((k) => {
            const o = person.owned[k];
            return (
              <div key={k} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-sm text-muted-foreground">{t(TYPE_LABEL[k])}</span>
                <Progress value={(o.done / (o.open + o.done)) * 100} className="h-2 flex-1" />
                <span className="w-24 shrink-0 text-end text-sm">{o.done} {t('done')} · {o.open} {t('open')}</span>
              </div>
            );
          })}
          {person.open + person.done === 0 && <p className="text-sm text-muted-foreground">{t('No owned items in the registers.')}</p>}
        </div>
      </section>
      {byPortfolio.map(([portfolio, projects]) => (
        <section key={portfolio} aria-label={portfolio}>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{tx(portfolio)} · {projects.length}</h3>
          <ul className="space-y-2">
            {projects.map((p, i) => (
              <li key={`${p.projectId}-${i}`} className="rounded-md border p-3">
                <p className="text-sm font-medium leading-snug">{tx(p.projectName)}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">{p.roles.map((r) => <Badge key={r} variant="secondary" className="text-xs">{tx(r)}</Badge>)}</div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * The delivery team on a tenant's projects: one entry per person across every project, with the
 * items they own (open and done). Read straight from project registers; nothing to approve.
 */
export function TeamDirectory({ fallback, below }: { fallback?: ReactNode; below?: ReactNode }) {
  const { t, tx } = useLocalization();
  const { people, totals, loading } = useTeamDirectory();
  const [open, setOpen] = useState<string | null>(null);

  const roleData = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of people) m.set(p.primaryRole, (m.get(p.primaryRole) ?? 0) + 1);
    return [...m].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [people]);
  const portfolioData = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of people) m.set(p.portfolio ?? t('No portfolio'), (m.get(p.portfolio ?? t('No portfolio')) ?? 0) + 1);
    return [...m].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [people, t]);
  const mixData = OWNED_TYPES.map((k) => ({ skill: t(TYPE_LABEL[k]), value: totals.byType[k] }));
  const loadData = people.slice(0, 15).map((p) => ({ name: shortName(p.name), open: p.open }));

  if (loading) return null;
  if (people.length === 0) return <>{fallback}</>;
  const selected = people.find((p) => p.key === open) ?? null;
  const avgOpen = Math.round(totals.open / people.length);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('Team Management')}</h1>
          <p className="mt-1 text-muted-foreground">{t('Who delivers your projects, what they own, and how loaded they are')}</p>
        </div>
        <Badge variant="outline" className="px-3 py-1 text-sm"><Users className="me-2 h-4 w-4" aria-hidden="true" />{people.length} {t('Members')}</Badge>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Stat icon={Users} tone="bg-primary/10 text-primary" label={t('Team Size')} value={people.length} />
        <Stat icon={CheckCircle2} tone="bg-emerald-500/10 text-emerald-500" label={t('Items Completed')} value={totals.done} />
        <Stat icon={BarChart3} tone="bg-amber-500/10 text-amber-500" label={t('Open Items')} value={totals.open} />
        <Stat icon={Clock} tone="bg-primary/10 text-primary" label={t('Avg Open Per Person')} value={avgOpen} />
      </div>

      <Tabs defaultValue="members" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="members">{t('Team Members')}</TabsTrigger>
          <TabsTrigger value="analytics">{t('Analytics')}</TabsTrigger>
          <TabsTrigger value="workload">{t('Workload')}</TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {people.map((p) => <MemberCard key={p.key} person={p} busiest={totals.busiest} onOpen={() => setOpen(p.key)} />)}
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-lg">{t('Role Distribution')}</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64" role="img" aria-label={t('Role Distribution')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={roleData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                        {roleData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-4">
                  {roleData.map((r, i) => (
                    <div key={r.name} className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-sm text-muted-foreground">{tx(r.name)} ({r.value})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">{t('What the Team Owns')}</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64" role="img" aria-label={t('What the Team Owns')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={mixData}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis dataKey="skill" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                      <PolarRadiusAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <Radar name={t('Items')} dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
                      <Tooltip contentStyle={tooltipStyle} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="text-lg">{t('People by Portfolio')}</CardTitle></CardHeader>
              <CardContent>
                <ul className="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-2">
                  {portfolioData.map((d) => (
                    <li key={d.name} className="flex items-center justify-between border-b border-border py-1.5 text-sm">
                      <span className="truncate text-muted-foreground">{tx(d.name)}</span><span className="font-medium">{d.value}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="workload" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-lg">{t('Open items per person')} <span className="text-sm font-normal text-muted-foreground">· {t('busiest 15')}</span></CardTitle></CardHeader>
            <CardContent>
              <div className="h-96" role="img" aria-label={t('Open items per person')}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={loadData} layout="vertical">
                    <XAxis type="number" allowDecimals={false} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis type="category" dataKey="name" interval={0} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} width={110} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="open" name={t('Open items')} fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {people.map((p) => {
              const pct = loadPct(p, totals.busiest);
              return (
                <Card key={p.key}>
                  <CardContent className="p-4">
                    <div className="mb-3 flex items-center gap-3">
                      <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary/10 text-sm text-primary">{initials(p.name)}</AvatarFallback></Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{tx(p.name)}</p>
                        <p className="truncate text-xs text-muted-foreground">{tx(p.primaryRole)}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('Current Load')}</span>
                        <span className={loadColor(pct)}>{p.open} {t('open')}</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                      <div className="flex justify-between pt-1 text-xs text-muted-foreground">
                        <span>{p.done} {t('done')}</span><span>{p.projects.length} {t('projects')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={selected !== null} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {selected && (
            <>
              <SheetHeader className="text-start">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12"><AvatarFallback className="bg-primary/10 text-primary">{initials(selected.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0">
                    <SheetTitle className="truncate">{tx(selected.name)}</SheetTitle>
                    <SheetDescription>{tx(selected.primaryRole)} · {selected.projects.length} {t('projects')} · {selected.open} {t('open')}, {selected.done} {t('done')}</SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              <div className="mt-6"><MemberDetail person={selected} /></div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {below}
    </div>
  );
}

export default TeamDirectory;
